import Refresh from '@mui/icons-material/Refresh';
import Visibility from '@mui/icons-material/Visibility';
import { Box, Button, InputAdornment, SvgIcon, TextField, Typography } from '@mui/material';
import CopyButton from 'components/@home/drawers/ProfileDrawer/Profile/EmployeeActions/InviteLink/CopyButton';
import DeleteDialog from 'components/common/DeleteDialog';
import PropTypes from 'prop-types';
import React, { forwardRef, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';

const SecretField = forwardRef(
  ({ name, value, confirmRegenerate, input: Input = TextField, slotProps, ...rest }, ref) => {
    const inputRef = useRef();
    const [visible, setVisible] = useState(false);
    const [confirmRegenerateVisible, setConfirmRegenerateVisible] = useState(false);

    const toggleVisible = () => {
      setVisible(prev => !prev);
    };

    const handleMouseDownPassword = event => {
      event.preventDefault();
      event.stopPropagation();
    };

    const select = () => {
      setTimeout(() => inputRef.current?.select?.(), 10);
    };

    const openConfirmRegenerate = () => {
      setConfirmRegenerateVisible(true);
    };

    const closeConfirmRegenerate = () => {
      setConfirmRegenerateVisible(false);
    };

    const handleConfirmRegenerate = () => {
      closeConfirmRegenerate();
      confirmRegenerate({ name, value });
    };

    return (
      <>
        <Input
          name={name}
          type={visible ? 'text' : 'password'}
          slotProps={{
            ...slotProps,
            htmlInput: {
              readOnly: true,
              ...slotProps?.htmlInput,
              onSelect: select,
              onMouseDown: select,
            },
            input: {
              sx: {
                backgroundColor: 'secondary.ultraLight',
              },
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    sx={{
                      display: 'flex',
                      height: 33.5,
                      gap: '4px',
                      mr: '-10px',
                    }}
                  >
                    <Button
                      sx={theme => ({
                        backgroundColor: theme.palette.white,
                        minHeight: 'auto',
                        '&:disabled .secret-field-icon': {
                          color: 'inherit',
                        },
                      })}
                      size="small"
                      disabled={visible}
                      variant="outlined"
                      color="secondary"
                      onClick={toggleVisible}
                      onMouseDown={handleMouseDownPassword}
                      disableElevation
                    >
                      <SvgIcon
                        className="secret-field-icon"
                        sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}
                      >
                        <Visibility />
                      </SvgIcon>
                      {I18n.t('SecretField.Show')}
                    </Button>
                    <CopyButton
                      value={value}
                      inputRef={inputRef}
                      size="small"
                      ButtonProps={{
                        size: 'small',
                        sx: theme => ({
                          backgroundColor: theme.palette.white,
                          minHeight: 'auto',
                        }),
                      }}
                    />
                  </Box>
                </InputAdornment>
              ),
              ...slotProps?.input,
            },
          }}
          {...rest}
          ref={ref}
          inputRef={inputRef}
        />
        <Button
          sx={{ ml: 1 }}
          size="small"
          variant="outlined"
          onClick={openConfirmRegenerate}
          disableElevation
        >
          <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
            <Refresh />
          </SvgIcon>
          {I18n.t('SecretField.Regenerate')}
        </Button>
        <DeleteDialog
          confirmText={I18n.t('SecretField.Regenerate')}
          message={I18n.t('SecretField.ConfirmRegenerateTitle', {
            name: I18n.t(`SecretField.${name}`),
          })}
          isOpen={confirmRegenerateVisible}
          onConfirm={handleConfirmRegenerate}
          onClose={closeConfirmRegenerate}
        >
          <Typography>
            {I18n.t('SecretField.ConfirmRegenerateDescription', {
              name: I18n.t(`SecretField.${name}`),
            })}
          </Typography>
        </DeleteDialog>
      </>
    );
  },
);

SecretField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  confirmRegenerate: PropTypes.func.isRequired,
  input: PropTypes.func,
};

export default SecretField;
